import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { Plus, Trash2, Upload, Download, Save, Users, UserPlus, ClipboardList, Star, X, CalendarRange, Link2, Pencil } from "lucide-react";
import PlanCard from "@/components/PlanCard";
import LiveScoresPanel from "@/components/LiveScoresPanel";
import { Switch } from "@/components/ui/switch";
import { GameweekSelect, pickCurrentGw } from "@/components/GameweekSelect";
import { DeadlineEditor } from "@/components/DeadlineEditor";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminCompetition() {
  const { id } = useParams();
  const [comp, setComp] = useState(null);
  const [stats, setStats] = useState(null);
  const [gws, setGws] = useState([]);
  const [activeGw, setActiveGw] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [members, setMembers] = useState([]);
  const [months, setMonths] = useState([]);
  const [newGw, setNewGw] = useState({ week_number: "", start_date: "", end_date: "", deadline: "" });
  const [newFx, setNewFx] = useState({ home_team: "", away_team: "", kickoff_at: "" });
  const [csvFile, setCsvFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [newManualName, setNewManualName] = useState("");
  const [entryPlayer, setEntryPlayer] = useState(null); // {id, name} — opens modal
  const [linkingPlayer, setLinkingPlayer] = useState(null); // {id, name} — opens link-account modal

  const loadAll = async () => {
    const [c, s, g, m, mo] = await Promise.all([
      api.get(`/competitions/${id}`),
      api.get(`/admin/dashboard/${id}`),
      api.get(`/competitions/${id}/gameweeks`),
      api.get(`/competitions/${id}/members`),
      api.get(`/competitions/${id}/months`),
    ]);
    setComp(c.data); setStats(s.data); setGws(g.data); setMembers(m.data); setMonths(mo.data);
    if (g.data.length && !activeGw) setActiveGw(pickCurrentGw(g.data));
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!activeGw) { setFixtures([]); return; }
    (async () => {
      const { data } = await api.get(`/competitions/${id}/fixtures?gameweek_id=${activeGw.id}`);
      setFixtures(data);
    })();
  }, [activeGw, id]);

  const [gwDeadlineAuto, setGwDeadlineAuto] = useState(true);
  const toLocalInput = (iso) => {
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const onGwStartChange = async (value) => {
    setNewGw((g) => ({ ...g, start_date: value }));
    if (!value || (!gwDeadlineAuto && newGw.deadline)) return;
    try {
      const { data } = await api.get(`/competitions/${id}/lock-suggestion`, { params: { after: new Date(value).toISOString() } });
      setNewGw((g) => ({ ...g, deadline: toLocalInput(data.deadline) }));
      setGwDeadlineAuto(true);
    } catch {
      /* leave deadline for manual entry */
    }
  };

  const addGw = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post(`/competitions/${id}/gameweeks`, {
        week_number: Number(newGw.week_number),
        start_date: new Date(newGw.start_date).toISOString(),
        end_date: new Date(newGw.end_date).toISOString(),
        deadline: new Date(newGw.deadline).toISOString(),
      });
      setNewGw({ week_number: "", start_date: "", end_date: "", deadline: "" });
      setGwDeadlineAuto(true);
      await loadAll();
    } catch (e2) {
      setMsg(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const deleteGw = async (gw) => {
    if (!window.confirm(`Delete GW${gw.week_number} and all its fixtures?`)) return;
    await api.delete(`/gameweeks/${gw.id}`);
    if (activeGw?.id === gw.id) setActiveGw(null);
    await loadAll();
  };

  const addFixture = async (e) => {
    e.preventDefault(); setMsg("");
    if (!activeGw) { setMsg("Select a gameweek first"); return; }
    try {
      const body = {
        gameweek_id: activeGw.id,
        home_team: newFx.home_team,
        away_team: newFx.away_team,
      };
      if (newFx.kickoff_at) body.kickoff_at = new Date(newFx.kickoff_at).toISOString();
      await api.post(`/competitions/${id}/fixtures`, body);
      setNewFx({ home_team: "", away_team: "", kickoff_at: "" });
      const { data } = await api.get(`/competitions/${id}/fixtures?gameweek_id=${activeGw.id}`);
      setFixtures(data);
    } catch (e2) {
      setMsg(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const updateFixtureScore = async (f, home_score, away_score) => {
    await api.patch(`/fixtures/${f.id}`, {
      home_score: home_score === "" ? null : Number(home_score),
      away_score: away_score === "" ? null : Number(away_score),
      status: (home_score !== "" && away_score !== "") ? "complete" : f.status,
    });
    const { data } = await api.get(`/competitions/${id}/fixtures?gameweek_id=${activeGw.id}`);
    setFixtures(data);
  };

  const deleteFixture = async (fid) => {
    if (!window.confirm("Delete fixture?")) return;
    await api.delete(`/fixtures/${fid}`);
    const { data } = await api.get(`/competitions/${id}/fixtures?gameweek_id=${activeGw.id}`);
    setFixtures(data);
  };

  const uploadCsv = async () => {
    if (!csvFile) return;
    const fd = new FormData();
    fd.append("file", csvFile);
    try {
      const res = await fetch(`${API}/competitions/${id}/fixtures/bulk-csv`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      setMsg(`Imported ${data.created} fixtures` + (data.errors?.length ? ` · ${data.errors.length} errors` : ""));
      setCsvFile(null);
      await loadAll();
    } catch (e) {
      setMsg(e.message);
    }
  };

  const addManualPlayer = async (e) => {
    e.preventDefault(); setMsg("");
    if (!newManualName.trim()) return;
    try {
      await api.post(`/competitions/${id}/manual-players`, { name: newManualName.trim() });
      setNewManualName("");
      const { data } = await api.get(`/competitions/${id}/members`);
      setMembers(data);
      setMsg("Player added.");
    } catch (e2) {
      setMsg(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from this competition?`)) return;
    try {
      await api.delete(`/competitions/${id}/members/${m.id}`);
      const { data } = await api.get(`/competitions/${id}/members`);
      setMembers(data);
    } catch (e2) {
      setMsg(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const saveMonth = async (payload, monthId) => {
    setMsg("");
    try {
      if (monthId) {
        await api.patch(`/months/${monthId}`, payload);
      } else {
        await api.post(`/competitions/${id}/months`, payload);
      }
      const { data } = await api.get(`/competitions/${id}/months`);
      setMonths(data);
      return true;
    } catch (e2) {
      setMsg(formatApiError(e2.response?.data?.detail) || e2.message);
      return false;
    }
  };

  const toggleSetting = async (key, on) => {
    const prev = comp[key];
    setComp((c) => ({ ...c, [key]: on }));
    try {
      await api.patch(`/competitions/${id}`, { [key]: on });
    } catch (e2) {
      setComp((c) => ({ ...c, [key]: prev }));
      setMsg(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const deleteMonth = async (m) => {
    if (!window.confirm(`Delete month "${m.name}"?`)) return;
    await api.delete(`/months/${m.id}`);
    const { data } = await api.get(`/competitions/${id}/months`);
    setMonths(data);
  };

  if (!comp) return <div className="text-[#AAB7C7]">Loading…</div>;

  return (
    <div className="fade-up space-y-6">
      <div>
        <Link to={`/app/competitions/${id}`} className="text-xs text-[#C7F500] hover:underline">← Back to {comp.name}</Link>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">Manage · {comp.name}</h1>
      </div>

      {/* Quick stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-4 rounded-xl bg-[#0D2038] border border-[#1F3A5C]"><div className="text-xs text-[#AAB7C7] uppercase">Members</div><div className="font-heading text-2xl font-bold">{stats.members}</div></div>
          <div className="p-4 rounded-xl bg-[#0D2038] border border-[#1F3A5C]"><div className="text-xs text-[#AAB7C7] uppercase">Fixtures</div><div className="font-heading text-2xl font-bold">{stats.fixtures_total}</div></div>
          <div className="p-4 rounded-xl bg-[#0D2038] border border-[#1F3A5C]"><div className="text-xs text-[#AAB7C7] uppercase">Awaiting Results</div><div className="font-heading text-2xl font-bold text-[#F5B942]">{stats.fixtures_awaiting_results}</div></div>
          <div className="p-4 rounded-xl bg-[#0D2038] border border-[#1F3A5C]"><div className="text-xs text-[#AAB7C7] uppercase">Predictions</div><div className="font-heading text-2xl font-bold">{stats.predictions}</div></div>
        </div>
      )}

      {/* Plan & Billing */}
      <PlanCard competition={comp} onChange={loadAll} />

      {/* Live Scores (Premier League) */}
      <LiveScoresPanel
        competitionId={id}
        gameweekId={activeGw?.id}
        fixtures={fixtures}
        onFixturesChanged={async () => {
          if (!activeGw) return;
          const { data } = await api.get(`/competitions/${id}/fixtures?gameweek_id=${activeGw.id}`);
          setFixtures(data);
        }}
      />

      {/* Gameweeks */}
      <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold">Gameweeks <span className="text-xs text-[#AAB7C7] font-normal">· 10 fixtures per week</span></h2>
          {gws.length > 0 && (
            <div className="flex items-center gap-2">
              <GameweekSelect gameweeks={gws} value={activeGw} onChange={setActiveGw} testId="admin-gw-select" />
              {activeGw && (
                <button onClick={() => deleteGw(activeGw)} data-testid="admin-gw-delete"
                  className="p-2 rounded-lg bg-[#132A45] hover:bg-red-900/50 text-[#AAB7C7] transition-colors" aria-label={`Delete GW${activeGw.week_number}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
        <form onSubmit={addGw} className="grid sm:grid-cols-5 gap-2 items-end" data-testid="add-gw-form">
          <div>
            <label className="text-xs text-[#AAB7C7] uppercase tracking-wider">Week #</label>
            <input required type="number" min={1} value={newGw.week_number} onChange={(e) => setNewGw({ ...newGw, week_number: e.target.value })}
              data-testid="gw-week-input" className="w-full bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="text-xs text-[#AAB7C7] uppercase tracking-wider">Start</label>
            <input required type="datetime-local" value={newGw.start_date} onChange={(e) => onGwStartChange(e.target.value)}
              data-testid="gw-start-new-input" className="w-full bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="text-xs text-[#AAB7C7] uppercase tracking-wider">End</label>
            <input required type="datetime-local" value={newGw.end_date} onChange={(e) => setNewGw({ ...newGw, end_date: e.target.value })}
              data-testid="gw-end-new-input" className="w-full bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="text-xs text-[#AAB7C7] uppercase tracking-wider">Deadline</label>
            <input required type="datetime-local" value={newGw.deadline} onChange={(e) => { setGwDeadlineAuto(false); setNewGw({ ...newGw, deadline: e.target.value }); }}
              data-testid="gw-deadline-new-input" className="w-full bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2 mt-1" />
          </div>
          <button type="submit" data-testid="add-gw-btn" className="px-3 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27]  text-[#071426] text-sm font-semibold inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add</button>
        </form>
      </div>

      {/* CSV import */}
      <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold">Bulk import fixtures</h2>
          <a href={`${API}/exports/fixtures-template.csv`} target="_blank" rel="noreferrer" data-testid="download-template"
            className="text-xs text-[#C7F500] hover:underline inline-flex items-center gap-1"><Download className="w-3 h-3" /> Template</a>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} data-testid="csv-file-input"
            className="text-sm text-[#AAB7C7] file:mr-3 file:bg-[#132A45] file:border-0 file:rounded-lg file:px-4 file:py-2 file:text-white file:font-medium" />
          <button onClick={uploadCsv} disabled={!csvFile} data-testid="csv-upload-btn"
            className="px-4 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] disabled:opacity-50  text-[#071426] text-sm font-semibold inline-flex items-center gap-2"><Upload className="w-4 h-4" /> Upload</button>
          <a href={`${API}/exports/${id}/fixtures.csv`} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-sm inline-flex items-center gap-2"><Download className="w-4 h-4" /> Export current</a>
        </div>
        <p className="mt-2 text-xs text-[#AAB7C7]">CSV columns: week_number, home_team, away_team, kickoff_at, deadline (optional). Missing gameweeks are auto-created.</p>
      </div>

      {/* Fixtures for active GW */}
      {activeGw && (
        <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-heading text-lg font-semibold">Fixtures — GW{activeGw.week_number}</h2>
            <div className="flex items-center gap-2">
            {fixtures.length > 0 && (
              <button
                type="button"
                onClick={() => window.open(`${API}/exports/${id}/fixture-sheet.pdf?gameweek_id=${activeGw.id}&t=${Date.now()}`, "_blank", "noreferrer")}
                data-testid="download-fixture-sheet"
                className="px-3 py-1.5 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Fixture sheet (PDF)
              </button>
            )}
            <span
              data-testid={`fixture-count-${activeGw.week_number}`}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full ${fixtures.length === 10 ? "bg-[#C7F500]/15 text-[#C7F500]" : "bg-[#F5B942]/15 text-[#F5B942]"}`}
            >
              {fixtures.length} / 10 fixtures
            </span>
            </div>
          </div>
          <DeadlineEditor key={activeGw.id} gameweek={activeGw} onSaved={(g) => { setActiveGw(g); loadAll(); }} />
          <form onSubmit={addFixture} className="grid sm:grid-cols-5 gap-2 mb-4 items-end" data-testid="add-fixture-form">
            <input required placeholder="Home team" value={newFx.home_team} onChange={(e) => setNewFx({ ...newFx, home_team: e.target.value })}
              data-testid="fx-home-input" className="sm:col-span-2 bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2" />
            <input required placeholder="Away team" value={newFx.away_team} onChange={(e) => setNewFx({ ...newFx, away_team: e.target.value })}
              data-testid="fx-away-input" className="sm:col-span-2 bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2" />
            <input type="datetime-local" value={newFx.kickoff_at} onChange={(e) => setNewFx({ ...newFx, kickoff_at: e.target.value })}
              data-testid="fx-kickoff-input" placeholder="Optional kickoff time"
              className="bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2 sm:col-span-4" />
            <button type="submit" data-testid="add-fixture-btn" className="px-3 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27]  text-[#071426] text-sm font-semibold inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add</button>
          </form>
          <div className="space-y-2">
            {fixtures.length === 0 && <p className="text-sm text-[#AAB7C7]">No fixtures yet.</p>}
            {fixtures.map((f) => (
              <FixtureRow key={f.id} f={f} onSave={updateFixtureScore} onDelete={deleteFixture} />
            ))}
          </div>
        </div>
      )}

      {/* Players (registered + manual) */}
      <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#C7F500]" />
            <h2 className="font-heading text-lg font-semibold">Players ({members.length})</h2>
          </div>
        </div>
        <form onSubmit={addManualPlayer} className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="add-manual-player-form">
          <input
            value={newManualName}
            onChange={(e) => setNewManualName(e.target.value)}
            placeholder="Add offline player (e.g. Uncle Bob)"
            data-testid="manual-player-name-input"
            className="flex-1 bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2"
          />
          <button type="submit" data-testid="add-manual-player-btn"
            className="px-4 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27]  text-[#071426] text-sm font-semibold inline-flex items-center justify-center gap-2">
            <UserPlus className="w-4 h-4" /> Add offline player
          </button>
        </form>
        <p className="text-xs text-[#AAB7C7] mb-3">
          Offline players can't log in — you enter their predictions and results for them from the "Enter" button below.
          Starting mid-season? Type each player's points so far into <b>B/F pts</b> — they're added to the Season table only.
        </p>
        <div className="divide-y divide-[#1F3A5C]">
          {members.map((m) => (
            <div key={m.id} className="py-3 flex items-center justify-between" data-testid={`member-row-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold text-xs ${m.is_manual ? "bg-[#F5B942]/20 text-[#F5B942]" : "bg-[#C7F500]/20 text-[#C7F500]"}`}>
                  {m.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {m.name}
                    {m.is_manual && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F5B942]/15 text-[#F5B942]">Offline</span>}
                  </div>
                  {!m.is_manual && <div className="text-xs text-[#AAB7C7]">{m.email}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <BroughtForwardInput competitionId={id} member={m} />
                {m.is_manual && (
                  <button
                    onClick={() => setLinkingPlayer({ id: m.id, name: m.name })}
                    data-testid={`link-account-${m.id}`}
                    className="px-3 py-1.5 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-xs font-medium inline-flex items-center gap-1 text-[#C7F500]"
                  >
                    <Link2 className="w-3.5 h-3.5" /> Link account
                  </button>
                )}
                <button
                  onClick={() => setEntryPlayer({ id: m.id, name: m.name })}
                  disabled={!activeGw}
                  data-testid={`enter-for-${m.id}`}
                  className="px-3 py-1.5 rounded-lg bg-[#132A45] hover:bg-[#1B4276] disabled:opacity-50 text-xs font-medium inline-flex items-center gap-1"
                >
                  <ClipboardList className="w-3.5 h-3.5" /> Enter GW{activeGw?.week_number ?? ""}
                </button>
                <button
                  onClick={() => removeMember(m)}
                  data-testid={`remove-member-${m.id}`}
                  className="p-1.5 rounded-lg bg-[#132A45] hover:bg-red-900/50"
                  aria-label={`Remove ${m.name}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {msg && <div className="text-sm text-[#C7F500]">{msg}</div>}

      {/* League settings */}
      <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C] space-y-4" data-testid="league-settings">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-lg font-semibold">Monthly table</h2>
            <p className="text-xs text-[#AAB7C7] mt-1">Turn off to run this league with just Weekly and Season tables.</p>
          </div>
          <Switch checked={comp.monthly_enabled !== false} onCheckedChange={(on) => toggleSetting("monthly_enabled", on)} data-testid="monthly-enabled-switch" className="data-[state=unchecked]:bg-[#3A5576] data-[state=checked]:bg-[#C7F500]" />
        </div>
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-[#1F3A5C] flex-wrap">
          <div>
            <h2 className="font-heading text-lg font-semibold">Weekly lock time</h2>
            <p className="text-xs text-[#AAB7C7] mt-1">UK time. New gameweeks get this deadline automatically; the countdown follows each week&apos;s actual deadline.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={comp.lock_weekday ?? 4} onChange={(e) => toggleSetting("lock_weekday", Number(e.target.value))} data-testid="lock-weekday-select"
              className="bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C7F500] focus:outline-none">
              {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
            <input type="time" value={comp.lock_time || "18:00"} onChange={(e) => e.target.value && toggleSetting("lock_time", e.target.value)} data-testid="lock-time-input"
              className="bg-[#071426] border border-[#1F3A5C] rounded-lg px-3 py-2 text-sm text-white focus:border-[#C7F500] focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-[#1F3A5C]">
          <div>
            <h2 className="font-heading text-lg font-semibold">Jokers</h2>
            <p className="text-xs text-[#AAB7C7] mt-1">One Joker per gameweek doubles a match&apos;s points. Turning off keeps past Joker points.</p>
          </div>
          <Switch checked={comp.joker_enabled !== false} onCheckedChange={(on) => toggleSetting("joker_enabled", on)} data-testid="joker-enabled-switch" className="data-[state=unchecked]:bg-[#3A5576] data-[state=checked]:bg-[#C7F500]" />
        </div>
      </div>

      {/* Custom Months */}
      {comp.monthly_enabled !== false && (
        <MonthsManager months={months} gws={gws} onSave={saveMonth} onDelete={deleteMonth} />
      )}

      {entryPlayer && activeGw && (
        <ManualEntryModal
          jokerOn={comp.joker_enabled !== false}
          competitionId={id}
          gameweek={activeGw}
          player={entryPlayer}
          onClose={() => { setEntryPlayer(null); loadAll(); }}
        />
      )}

      {linkingPlayer && (
        <LinkAccountModal
          player={linkingPlayer}
          onClose={() => setLinkingPlayer(null)}
          onLinked={async () => {
            setLinkingPlayer(null);
            const { data } = await api.get(`/competitions/${id}/members`);
            setMembers(data);
            setMsg(`Account linked for ${linkingPlayer.name}. They can now log in.`);
          }}
        />
      )}
    </div>
  );
}

function LinkAccountModal({ player, onClose, onLinked }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/users/${player.id}/link-account`, { email: email.trim(), password });
      onLinked();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-[#0D2038] border border-[#1F3A5C] rounded-2xl p-6 fade-up"
        data-testid="link-account-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl tracking-wide">Link {player.name}'s account</h2>
            <p className="text-xs text-[#AAB7C7] mt-1">
              Their predictions and points stay exactly as they are — this just lets them log in.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg bg-[#132A45] hover:bg-[#1B4276]" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#AAB7C7]">Email</label>
            <input
              type="email" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="link-email-input"
              className="mt-1 w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5"
              placeholder="player@example.com"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#AAB7C7]">Temporary password</label>
            <input
              type="text" required minLength={6}
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="link-password-input"
              className="mt-1 w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5"
              placeholder="At least 6 characters"
            />
            <p className="text-[10px] text-[#AAB7C7] mt-1">
              Share this with the player — they can change it once logged in via Forgot password.
            </p>
          </div>
          {err && <div className="text-sm text-red-400" data-testid="link-account-error">{err}</div>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="link-account-submit"
            className="px-4 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] disabled:opacity-50 text-[#071426] text-sm font-bold uppercase tracking-wide inline-flex items-center gap-2">
            <Link2 className="w-4 h-4" /> {busy ? "Linking…" : "Link account"}
          </button>
        </div>
      </form>
    </div>
  );
}

function MonthsManager({ months, gws, onSave, onDelete }) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [editingId, setEditingId] = useState(null);

  const reset = () => {
    setEditingId(null);
    setName("");
    setSelected(new Set());
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setName(m.name);
    setSelected(new Set(m.gameweek_ids || []));
  };

  const toggle = (gwId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(gwId)) next.delete(gwId);
      else next.add(gwId);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || selected.size === 0) return;
    if (await onSave({ name: name.trim(), gameweek_ids: Array.from(selected) }, editingId)) reset();
  };

  const remove = (m) => {
    if (m.id === editingId) reset();
    onDelete(m);
  };

  return (
    <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="months-manager">
      <div className="flex items-center gap-2 mb-3">
        <CalendarRange className="w-5 h-5 text-[#C7F500]" />
        <h2 className="font-heading text-lg font-semibold">Monthly windows</h2>
      </div>
      <p className="text-xs text-[#AAB7C7] mb-4">
        Define which gameweeks make up each month for the Monthly leaderboard (e.g. "September" = weeks 4-8).
      </p>

      {months.length > 0 && (
        <div className="space-y-2 mb-4">
          {months.map((m) => {
            const weeks = gws
              .filter((g) => (m.gameweek_ids || []).includes(g.id))
              .map((g) => g.week_number)
              .sort((a, b) => a - b);
            return (
              <div key={m.id} className={`flex items-center justify-between p-3 rounded-xl bg-[#071426] border ${editingId === m.id ? "border-[#C7F500]" : "border-[#1F3A5C]"}`} data-testid={`month-row-${m.id}`}>
                <div>
                  <div className="font-medium">{m.name}</div>
                  <div className="text-xs text-[#AAB7C7]" data-testid={`month-weeks-${m.id}`}>
                    {weeks.length ? `GW ${weeks.join(", ")}` : "No gameweeks assigned"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(m)} data-testid={`month-edit-${m.id}`}
                    className="p-1.5 rounded-lg bg-[#132A45] hover:bg-[#1F3A5C] transition-colors" aria-label={`Edit ${m.name}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(m)} data-testid={`month-delete-${m.id}`}
                    className="p-1.5 rounded-lg bg-[#132A45] hover:bg-red-900/50 transition-colors" aria-label={`Delete ${m.name}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <form onSubmit={submit} className="space-y-3" data-testid="add-month-form">
        {editingId && (
          <p className="text-xs text-[#C7F500]" data-testid="month-editing-label">Editing "{months.find((m) => m.id === editingId)?.name}" — toggle weeks, then save.</p>
        )}
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Month name (e.g. September)"
          data-testid="month-name-input"
          className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2"
        />
        {gws.length === 0 ? (
          <p className="text-xs text-[#AAB7C7]">Create some gameweeks first — then assign them here.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {[...gws].sort((a, b) => a.week_number - b.week_number).map((g) => {
              const on = selected.has(g.id);
              return (
                <button
                  key={g.id} type="button"
                  onClick={() => toggle(g.id)}
                  data-testid={`month-gw-toggle-${g.week_number}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${on ? "bg-[#C7F500] border-[#C7F500] text-[#071426]" : "bg-[#132A45] border-[#1F3A5C] text-[#AAB7C7] hover:text-white"}`}
                >
                  GW{g.week_number}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-2">
          <button type="submit" disabled={!name.trim() || selected.size === 0}
            data-testid="add-month-btn"
            className="px-4 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] disabled:opacity-50  text-[#071426] text-sm font-semibold inline-flex items-center gap-2">
            {editingId ? <><Save className="w-4 h-4" /> Save changes</> : <><Plus className="w-4 h-4" /> Add month</>}
          </button>
          {editingId && (
            <button type="button" onClick={reset} data-testid="month-cancel-edit-btn"
              className="px-4 py-2 rounded-lg bg-[#132A45] hover:bg-[#1F3A5C] text-sm font-semibold inline-flex items-center gap-2 transition-colors">
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function FixtureRow({ f, onSave, onDelete }) {
  const [home, setHome] = useState(f.home_score ?? "");
  const [away, setAway] = useState(f.away_score ?? "");
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-[#071426] border border-[#1F3A5C]" data-testid={`admin-fixture-${f.id}`}>
      <div className="flex-1 text-right font-medium pr-2 truncate">{f.home_team}</div>
      <input type="number" min={0} value={home} onChange={(e) => setHome(e.target.value)} className="score-input" data-testid={`fx-home-score-${f.id}`} />
      <span className="text-[#7A8AA3]">-</span>
      <input type="number" min={0} value={away} onChange={(e) => setAway(e.target.value)} className="score-input" data-testid={`fx-away-score-${f.id}`} />
      <div className="flex-1 pl-2 truncate">{f.away_team}</div>
      <button onClick={() => onSave(f, home, away)} data-testid={`save-result-${f.id}`}
        className="px-3 py-1.5 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27]  text-[#071426] text-xs font-semibold inline-flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Save</button>
      <button onClick={() => onDelete(f.id)} className="p-1.5 rounded-lg bg-[#132A45] hover:bg-red-900/50" aria-label="Delete fixture">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function BroughtForwardInput({ competitionId, member }) {
  const [value, setValue] = useState(String(member.brought_forward_points || 0));
  const [state, setState] = useState("idle");

  const save = async () => {
    const pts = Math.max(0, parseInt(value, 10) || 0);
    setValue(String(pts));
    if (pts === (member.brought_forward_points || 0)) return;
    setState("saving");
    try {
      await api.put(`/competitions/${competitionId}/members/${member.id}/brought-forward`, { points: pts });
      member.brought_forward_points = pts;
      setState("saved");
    } catch {
      setState("error");
    }
  };

  return (
    <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#AAB7C7]" title="Season points carried over from before this league joined">
      B/F pts
      <input
        type="number" min={0} inputMode="numeric" value={value}
        onChange={(e) => { setValue(e.target.value); setState("idle"); }}
        onBlur={save}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        data-testid={`brought-forward-${member.id}`}
        className={`w-16 bg-[#071426] border rounded-lg px-2 py-1 text-sm text-white text-center focus:outline-none transition-colors ${state === "error" ? "border-red-500" : state === "saved" ? "border-[#C7F500]" : "border-[#1F3A5C] focus:border-[#C7F500]"}`}
      />
    </label>
  );
}

function ManualEntryModal({ competitionId, gameweek, player, onClose, jokerOn }) {
  const [fixtures, setFixtures] = useState([]);
  const [entries, setEntries] = useState({}); // { fixture_id: {home, away, is_joker} }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      const [fx, grid] = await Promise.all([
        api.get(`/competitions/${competitionId}/fixtures?gameweek_id=${gameweek.id}`),
        api.get(`/admin/predictions/grid?competition_id=${competitionId}&gameweek_id=${gameweek.id}`),
      ]);
      setFixtures(fx.data);
      // seed entries from existing predictions for this player
      const seeded = {};
      for (const p of grid.data.predictions) {
        if (p.user_id !== player.id) continue;
        seeded[p.fixture_id] = { home: String(p.home_goals), away: String(p.away_goals), is_joker: !!p.is_joker };
      }
      setEntries(seeded);
    })();
  }, [competitionId, gameweek.id, player.id]);

  const setEntry = (fid, patch) => {
    setEntries((prev) => {
      const next = { ...prev, [fid]: { home: "", away: "", is_joker: false, ...prev[fid], ...patch } };
      // enforce single joker within this form
      if (patch.is_joker === true) {
        for (const k of Object.keys(next)) {
          if (k !== fid && next[k]) next[k] = { ...next[k], is_joker: false };
        }
      }
      return next;
    });
  };

  const saveAll = async () => {
    setBusy(true); setMsg("");
    let saved = 0, skipped = 0, errors = 0;
    for (const f of fixtures) {
      const e = entries[f.id];
      if (!e || e.home === "" || e.away === "") { skipped++; continue; }
      try {
        await api.put("/admin/predictions", {
          fixture_id: f.id,
          user_id: player.id,
          home_goals: Number(e.home),
          away_goals: Number(e.away),
          is_joker: !!e.is_joker,
        });
        saved++;
      } catch {
        errors++;
      }
    }
    setBusy(false);
    setMsg(`Saved ${saved}${skipped ? ` · ${skipped} skipped` : ""}${errors ? ` · ${errors} errors` : ""}`);
    if (!errors) setTimeout(onClose, 900);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0D2038] border border-[#1F3A5C] rounded-2xl p-6 fade-up" data-testid="manual-entry-modal">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="font-heading text-xl font-bold">Enter predictions for {player.name}</h2>
            <p className="text-xs text-[#AAB7C7] mt-1">Gameweek {gameweek.week_number} · admin entry bypasses deadline</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg bg-[#132A45] hover:bg-[#1B4276]" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {fixtures.length === 0 && <p className="text-sm text-[#AAB7C7]">No fixtures in this gameweek yet.</p>}
          {fixtures.map((f) => {
            const e = entries[f.id] || { home: "", away: "", is_joker: false };
            return (
              <div key={f.id} className={`flex items-center gap-2 p-3 rounded-xl bg-[#071426] border ${e.is_joker ? "border-[#F5B942]/40 joker-glow" : "border-[#1F3A5C]"}`}>
                <div className="flex-1 text-right pr-2 font-medium truncate">{f.home_team}</div>
                <input type="number" min={0} value={e.home}
                  onChange={(ev) => setEntry(f.id, { home: ev.target.value })}
                  data-testid={`entry-home-${f.id}`}
                  className="score-input" />
                <span className="text-[#7A8AA3]">-</span>
                <input type="number" min={0} value={e.away}
                  onChange={(ev) => setEntry(f.id, { away: ev.target.value })}
                  data-testid={`entry-away-${f.id}`}
                  className="score-input" />
                <div className="flex-1 pl-2 font-medium truncate">{f.away_team}</div>
                {jokerOn && <button type="button"
                  onClick={() => setEntry(f.id, { is_joker: !e.is_joker })}
                  data-testid={`entry-joker-${f.id}`}
                  className={`p-2 rounded-lg border ${e.is_joker ? "bg-[#F5B942]/15 border-[#F5B942] text-[#F5B942]" : "bg-[#132A45] border-[#1F3A5C] text-[#AAB7C7]"}`}
                  aria-label="Joker"
                >
                  <Star className={`w-4 h-4 ${e.is_joker ? "fill-[#F5B942]" : ""}`} />
                </button>}
              </div>
            );
          })}
        </div>

        {msg && <div className="mt-3 text-sm text-[#C7F500]">{msg}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-sm">Cancel</button>
          <button onClick={saveAll} disabled={busy || fixtures.length === 0} data-testid="save-manual-entries"
            className="px-4 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] disabled:opacity-50  text-[#071426] text-sm font-semibold inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save predictions"}
          </button>
        </div>
      </div>
    </div>
  );
}
